import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { useBillingCycles } from "@/react/hooks/useBillingCycles";

export interface DateRange {
  endDate: number;
  label: string;
  startDate: number;
  value: string;
}

interface UseDateRangesProps {
  onDateRangeSelect?: (range: DateRange) => void;
}

export const useDateRanges = ({
  onDateRangeSelect,
}: UseDateRangesProps = {}) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { billingCycles, isLoading, hasData, refetch } = useBillingCycles({
    enabled: shouldFetch,
    products: ["ai_actions"],
  });

  const formatDateRangeLabel = useCallback(
    (
      startDate: Date,
      endDate: Date,
      isCurrent: boolean,
      isFirstCycle: boolean,
    ) => {
      const startFormatted = format(startDate, "MMM d");

      if (isCurrent || isFirstCycle) {
        return t("settings.billing.ai_actions.date_ranges.range_format", {
          start: startFormatted,
          end: t("settings.billing.ai_actions.date_ranges.present"),
        });
      }

      const isSameDay = startDate.toDateString() === endDate.toDateString();
      if (isSameDay) {
        return t("settings.billing.ai_actions.date_ranges.range_format", {
          start: startFormatted,
          end: format(endDate, "MMM d"),
        });
      }

      const isSameMonth =
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear();

      if (isSameMonth) {
        return t("settings.billing.ai_actions.date_ranges.range_format", {
          start: startFormatted,
          end: format(endDate, "d"),
        });
      }

      return t("settings.billing.ai_actions.date_ranges.range_format", {
        start: startFormatted,
        end: format(endDate, "MMM d"),
      });
    },
    [],
  );

  const dateRanges: DateRange[] = useMemo(() => {
    if (!hasData) {
      return [];
    }

    return billingCycles
      .sort((a, b) => b.start_date - a.start_date) // Sort by start_date descending (newest first)
      .map(cycle => {
        const startDate = new Date(cycle.start_date * 1000);
        const endDate = new Date(cycle.end_date * 1000);
        const now = new Date();

        const isCurrent = startDate <= now && now <= endDate;
        const isFirstCycle = billingCycles[0]?.id === cycle.id;

        return {
          label: formatDateRangeLabel(
            startDate,
            endDate,
            isCurrent,
            isFirstCycle,
          ),
          value: cycle.id,
          startDate: cycle.start_date,
          endDate: cycle.end_date,
        };
      });
  }, [billingCycles, hasData, formatDateRangeLabel]);

  const handleDateRangeSelect = (range: DateRange) => {
    onDateRangeSelect?.(range);
  };

  const fetchBillingCycles = useCallback(() => {
    if (!hasData && !isLoading) {
      setShouldFetch(true);
    } else if (hasData) {
      void refetch();
    }
  }, [hasData, isLoading, refetch]);

  return {
    dateRanges,
    handleDateRangeSelect,
    isLoading,
    hasData,
    fetchBillingCycles,
  };
};
